/**
 * Estructura unificada de error de la API (ApiErrorResponse).
 * La API devuelve: { message, errors?, detail? }
 */
export interface ApiErrorResponse {
  message: string;
  errors?: Record<string, string[]>;
  detail?: string;
}

function joinApiErrorMessages(messages: string[]): string {
  const unique = Array.from(new Set(messages.map((m) => m.trim()).filter(Boolean)));
  if (unique.length === 0) return '';
  if (unique.length === 1) return unique[0];
  return unique.join('\n');
}

const MAX_USER_ERROR_LENGTH = 220;

/** Recorta páginas de excepción de ASP.NET / stack traces para mensajes de UI. */
export function sanitizeApiErrorTextForUser(text: string): string {
  let raw = text.trim();
  if (!raw) return '';

  const headersIdx = raw.search(/\nHEADERS\s*\n/i);
  if (headersIdx > 0) raw = raw.slice(0, headersIdx).trim();

  const knownPatterns = [
    /Invalid column name '[^']+'\.?/i,
    /Invalid object name '[^']+'\.?/i,
    /Cannot insert duplicate key[^.\n]*/i,
    /Timeout expired[^.\n]*/i,
    /Login failed for user[^.\n]*/i,
    /No se pudo actualizar la base de datos del cliente[^.\n]*/i,
  ];
  for (const pattern of knownPatterns) {
    const match = raw.match(pattern);
    if (match?.[0]) return match[0].trim();
  }

  const stackIdx = raw.search(/\s+at [A-Za-z0-9_.]+\./);
  if (stackIdx > 0) raw = raw.slice(0, stackIdx).trim();

  const lines = raw.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  let line = lines[0] ?? raw;

  const exceptionMsg = line.match(
    /(?:SqlException|InvalidOperationException|Exception)[^:]*:\s*(.+)$/i,
  );
  if (exceptionMsg?.[1]) {
    line = exceptionMsg[1].trim();
  }

  if (line.length > MAX_USER_ERROR_LENGTH) {
    return `${line.slice(0, MAX_USER_ERROR_LENGTH).trim()}…`;
  }
  return line;
}

function sanitizeIfLong(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return '';
  if (trimmed.length <= MAX_USER_ERROR_LENGTH && !trimmed.includes('\n') && !/(\s+at\s+\w)/.test(trimmed)) {
    return trimmed;
  }
  return sanitizeApiErrorTextForUser(trimmed);
}

/** Registra el error completo de la API en consola (solo útil en desarrollo). */
export function logApiErrorDetail(error: unknown, context?: string): void {
  if (process.env.NODE_ENV === 'production') return;

  const prefix = context ? `[API] ${context}` : '[API]';
  const err = error as {
    response?: { status?: number; data?: unknown; config?: { url?: string; method?: string } };
    message?: string;
  };

  if (err.response) {
    console.error(prefix, {
      status: err.response.status,
      url: err.response.config?.url,
      method: err.response.config?.method,
      data: err.response.data,
    });
    return;
  }

  console.error(prefix, error);
}

function collectErrorsFromRecord(errors: Record<string, unknown>): string[] {
  const errorMessages: string[] = [];
  for (const key of Object.keys(errors)) {
    const value = errors[key];
    if (Array.isArray(value)) {
      errorMessages.push(...value.filter((v): v is string => typeof v === 'string'));
    } else if (typeof value === 'string') {
      errorMessages.push(value);
    }
  }
  return errorMessages;
}

/**
 * Extrae el mensaje de error de una respuesta de la API para mostrarlo al usuario.
 * Soporta la estructura unificada { message, errors?, detail? } y formatos legacy:
 * - JSON con { message } o { mensaje }
 * - Cuerpo en texto plano (string)
 * - ModelState de ASP.NET Core con { message, errors }
 */
export function getApiErrorMessage(
  error: unknown,
  defaultMessage: string = 'Ha ocurrido un error'
): string {
  if (error == null) return defaultMessage;

  const data = (error as { response?: { data?: unknown } })?.response?.data;

  if (typeof data === 'string' && data.trim()) {
    return sanitizeIfLong(data) || defaultMessage;
  }

  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;

    // Priorizar errors.Items u otros errores de validación antes del message genérico.
    if (obj.errors && typeof obj.errors === 'object' && !Array.isArray(obj.errors)) {
      const errorMessages = collectErrorsFromRecord(obj.errors as Record<string, unknown>);
      if (errorMessages.length > 0) {
        return joinApiErrorMessages(errorMessages);
      }
    }

    const possibleModelStateErrors: string[] = [];
    for (const key in obj) {
      const value = obj[key];
      if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
        possibleModelStateErrors.push(...(value as string[]));
      }
    }
    if (possibleModelStateErrors.length > 0) {
      return joinApiErrorMessages(possibleModelStateErrors);
    }

    const msg =
      obj.message ??
      obj.mensaje ??
      obj.Message ??
      obj.Mensaje ??
      obj.detail ??
      obj.error ??
      obj.title;
    if (msg != null && typeof msg === 'string' && msg.trim()) {
      return sanitizeIfLong(msg) || defaultMessage;
    }
    if (Array.isArray(obj.errors) && obj.errors.length > 0) {
      const first = obj.errors[0];
      if (typeof first === 'string') return first;
      if (first && typeof first === 'object' && 'message' in first) {
        return String((first as { message?: string }).message ?? defaultMessage);
      }
    }
  }

  const message = (error as Error)?.message;
  if (typeof message === 'string' && message.trim()) {
    if (/^network error$/i.test(message.trim())) {
      return 'No hubo respuesta del servidor. Si estabas subiendo un archivo, recargá e intentá de nuevo; si sigue, revisá IIS y permisos de Storage.';
    }
    return sanitizeIfLong(message) || defaultMessage;
  }

  return defaultMessage;
}

export async function getApiErrorMessageAsync(
  error: unknown,
  defaultMessage: string = 'Ha ocurrido un error',
): Promise<string> {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text) as unknown;
      return getApiErrorMessage({ response: { data: parsed } }, defaultMessage);
    } catch {
      return defaultMessage;
    }
  }
  return getApiErrorMessage(error, defaultMessage);
}

/** Si el blob no es PDF (p. ej. JSON de error con status 200), lanza con el mensaje del API. */
export async function asPdfBlobOrThrow(blob: Blob, fallback: string): Promise<Blob> {
  const type = (blob.type || '').toLowerCase();
  if (type.includes('pdf') || type === 'application/octet-stream' || type === '') {
    return blob;
  }
  if (type.includes('json') || type.includes('text')) {
    const text = await blob.text();
    try {
      const parsed = JSON.parse(text) as { message?: string; error?: string; Message?: string };
      throw new Error(parsed.message || parsed.error || parsed.Message || fallback);
    } catch (e) {
      if (e instanceof SyntaxError) throw new Error(fallback);
      throw e;
    }
  }
  return blob;
}
